/**
 * Custom fetch wrapper that handles session cookies and error responses
 * Provides interceptor-like behavior for the native fetch API
 */

// Relative, as everywhere: Caddy serves the apps and the API on one origin, in
// development as in production. The absolute localhost:8000 this used to fall
// back to predates that, and would bypass the proxy — and its cookie — entirely.
export const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * Make an API call with automatic session cookie handling and 401 error handling
 * @param {string} url - Relative URL (e.g., '/users')
 * @param {object} options - Fetch options (method, body, headers, etc.)
 * @param {object} toast - Toast context object from useToast()
 * @param {function} logout - Logout callback from useAuth() to trigger logout
 * @returns {Promise<Response>} - Fetch response
 */
export async function apiCall(url, options = {}, toast = null, logout = null) {
  // A file goes as multipart, and only the browser knows the boundary it
  // writes into that header: setting it here would break the upload.
  const isForm = typeof FormData !== 'undefined' && options.body instanceof FormData;
  const headers = {
    ...(isForm ? {} : { 'Content-Type': 'application/json' }),
    ...options.headers,
  };

  const fullUrl = url.startsWith('http') ? url : `${API_BASE_URL}${url}`;

  const response = await fetch(fullUrl, {
    ...options,
    credentials: 'include', // Include cookies for session
    headers,
  });

  // Handle 401 Unauthorized
  if (response.status === 401) {
    if (toast && toast.error) {
      toast.error('Session expirée — reconnectez-vous');
    }

    // Call logout callback if provided to update AuthContext
    if (logout && typeof logout === 'function') {
      logout();
    }
  }

  return response;
}

export default apiCall;
